import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { v4 as uuid } from 'uuid';

import { Button } from '@/components/ui/button';
import { useApp, type Attachment } from '@/store/useApp';

interface ComposerProps {
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onStopAndDiscard: () => void;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB — Anthropic image limit
const MAX_PDF_BYTES = 32 * 1024 * 1024; // 32 MB — Anthropic PDF limit
const MAX_TEXT_BYTES = 200 * 1024; // 200 KB — soft cap to keep prompt size reasonable
const ACCEPT_LIST = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'image/svg+xml',
  'text/html',
  'text/css',
  'text/plain',
  'text/markdown',
  'application/json',
  'text/javascript',
  '.svg',
  '.html',
  '.htm',
  '.css',
  '.txt',
  '.md',
  '.json',
  '.js',
].join(',');

const IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

function isImageMime(mime: string): mime is (typeof IMAGE_MIMES)[number] {
  return (IMAGE_MIMES as readonly string[]).includes(mime);
}

function inferMediaType(file: File): string {
  if (file.type) return file.type;
  // Some browsers don't set MIME on .svg / .md / etc. Fall back to extension sniffing.
  const ext = /\.([^.]+)$/.exec(file.name)?.[1]?.toLowerCase();
  switch (ext) {
    case 'svg':
      return 'image/svg+xml';
    case 'html':
    case 'htm':
      return 'text/html';
    case 'css':
      return 'text/css';
    case 'json':
      return 'application/json';
    case 'md':
      return 'text/markdown';
    case 'txt':
      return 'text/plain';
    case 'js':
    case 'mjs':
      return 'text/javascript';
    default:
      return 'application/octet-stream';
  }
}

async function fileToAttachment(file: File): Promise<Attachment | { error: string; name: string }> {
  const mediaType = inferMediaType(file);

  if (isImageMime(mediaType)) {
    if (file.size > MAX_IMAGE_BYTES) {
      return { error: `${formatSize(file.size)} exceeds the 5 MB image limit`, name: file.name };
    }
    const dataUrl = await readAsDataURL(file);
    const data = dataUrl.includes(',') ? dataUrl.split(',', 2)[1]! : dataUrl;
    return {
      id: uuid(),
      kind: 'image',
      mediaType,
      data,
      dataUrl,
      bytes: file.size,
      name: file.name,
    };
  }

  if (mediaType === 'application/pdf') {
    if (file.size > MAX_PDF_BYTES) {
      return { error: `${formatSize(file.size)} exceeds the 32 MB PDF limit`, name: file.name };
    }
    const dataUrl = await readAsDataURL(file);
    const data = dataUrl.includes(',') ? dataUrl.split(',', 2)[1]! : dataUrl;
    return {
      id: uuid(),
      kind: 'document',
      mediaType: 'application/pdf',
      data,
      bytes: file.size,
      name: file.name,
    };
  }

  // Text-based files — embed as text in the prompt.
  const TEXT_MIMES = [
    'image/svg+xml',
    'text/html',
    'text/css',
    'text/plain',
    'text/markdown',
    'application/json',
    'text/javascript',
    'text/x-typescript',
  ];
  if (TEXT_MIMES.includes(mediaType) || file.size <= MAX_TEXT_BYTES) {
    if (file.size > MAX_TEXT_BYTES) {
      return {
        error: `${formatSize(file.size)} exceeds the 200 KB text-file limit`,
        name: file.name,
      };
    }
    const content = await file.text();
    return {
      id: uuid(),
      kind: 'text-file',
      mediaType,
      content,
      bytes: file.size,
      name: file.name,
    };
  }

  return {
    error: `${file.name} (${mediaType || 'unknown type'}) — Claude can read PNG/JPEG/GIF/WebP, PDFs, and text/code files. Binary design files (Figma, Sketch, PSD, AI) need to be exported first.`,
    name: file.name,
  };
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Composer({ isStreaming, onSend, onStop, onStopAndDiscard }: ComposerProps) {
  const draft = useApp((s) => s.composerDraft);
  const setDraft = useApp((s) => s.setComposerDraft);
  const attachments = useApp((s) => s.attachments);
  const addAttachment = useApp((s) => s.addAttachment);
  const removeAttachment = useApp((s) => s.removeAttachment);
  const includeScript = useApp((s) => s.includeScript);
  const setIncludeScript = useApp((s) => s.setIncludeScript);
  const [value, setValue] = useState(draft);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (draft && draft !== value) setValue(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const send = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && attachments.length === 0) return;
    onSend(trimmed);
    setValue('');
    setDraft('');
    setErrors([]);
  }, [attachments.length, onSend, setDraft, value]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!isStreaming) send();
    }
  };

  async function ingestFiles(files: FileList | File[]): Promise<void> {
    const list = Array.from(files);
    if (list.length === 0) return;
    const newErrors: string[] = [];
    for (const file of list) {
      const result = await fileToAttachment(file);
      if ('error' in result) {
        newErrors.push(result.error);
      } else {
        addAttachment(result);
      }
    }
    setErrors(newErrors);
  }

  const onPaste = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!e.clipboardData) return;
    const fileItems = Array.from(e.clipboardData.items).filter((i) => i.kind === 'file');
    if (fileItems.length === 0) return;
    e.preventDefault();
    const files = fileItems
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    await ingestFiles(files);
  };

  function onDragEnter(e: DragEvent) {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setIsDragging(true);
    }
  }
  function onDragOver(e: DragEvent) {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }
  function onDragLeave(e: DragEvent) {
    // Only clear when truly leaving the composer (not when crossing inner elements).
    if (e.currentTarget === e.target) setIsDragging(false);
  }
  async function onDrop(e: DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      await ingestFiles(e.dataTransfer.files);
    }
  }

  return (
    <div
      className={`relative flex flex-col gap-2 border-t border-neutral-200 p-3 transition-colors dark:border-neutral-800 ${
        isDragging ? 'bg-blue-50/60 dark:bg-blue-950/30' : ''
      }`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e) => void onDrop(e)}
    >
      {isDragging && (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-blue-400 bg-blue-50/80 text-sm text-blue-700 dark:bg-blue-950/60 dark:text-blue-200"
          aria-hidden="true"
        >
          Drop files to attach (images, PDFs, SVG/HTML/CSS/text)
        </div>
      )}

      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <AttachmentChip key={a.id} attachment={a} onRemove={() => removeAttachment(a.id)} />
          ))}
        </ul>
      )}

      {errors.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-[11px] text-amber-700 dark:text-amber-400">
          {errors.map((err, i) => (
            <li key={i} role="alert">
              {err}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isStreaming}
          title="Attach files (images, PDFs, SVG, HTML, CSS, code)"
          aria-label="Attach files"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M15.621 4.379a3 3 0 0 0-4.242 0l-7 7a5 5 0 0 0 7.071 7.071l5.657-5.657a.75.75 0 0 0-1.06-1.06l-5.657 5.656a3.5 3.5 0 1 1-4.95-4.95l7-7a1.5 1.5 0 1 1 2.121 2.121l-7 7a.5.5 0 0 1-.707-.707l5.657-5.657a.75.75 0 0 0-1.06-1.06l-5.658 5.657a2 2 0 0 0 2.828 2.828l7-7a3 3 0 0 0 0-4.242Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_LIST}
          hidden
          onChange={(e) => {
            if (e.target.files) void ingestFiles(e.target.files);
            e.target.value = ''; // allow re-picking the same file
          }}
        />
        <button
          type="button"
          onClick={() => setIncludeScript(!includeScript)}
          title={includeScript ? 'Full script source will be sent to the model' : 'Script source is summarised — click to include full source'}
          aria-pressed={includeScript}
          className={`flex h-9 shrink-0 items-center rounded-md border px-2 text-[11px] font-medium transition-colors ${
            includeScript
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/50 dark:text-blue-300'
              : 'border-neutral-300 bg-white text-neutral-400 hover:text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:text-neutral-300'
          }`}
        >
          {'</>'}
        </button>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={(e) => void onPaste(e)}
          placeholder={
            isStreaming
              ? 'Streaming…'
              : 'Describe the design or refine it. Drop images, PDFs, SVG, or code files for context.'
          }
          rows={3}
          className="min-h-[44px] w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-900"
          disabled={isStreaming}
        />
        {isStreaming ? (
          <div className="flex shrink-0 items-stretch overflow-hidden rounded-md">
            <Button
              variant="destructive"
              onClick={onStop}
              className="rounded-r-none"
              title="Stop generation but keep the partial result"
            >
              Stop
            </Button>
            <Button
              variant="destructive"
              onClick={onStopAndDiscard}
              className="rounded-l-none border-l border-red-500/40 px-2"
              title="Stop and discard — drop the in-flight version, keep the prior preview"
              aria-label="Stop and discard"
            >
              ✕
            </Button>
          </div>
        ) : (
          <Button onClick={send} disabled={!value.trim() && attachments.length === 0}>
            Send
          </Button>
        )}
      </div>
    </div>
  );
}

function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: Attachment;
  onRemove: () => void;
}) {
  if (attachment.kind === 'image') {
    return (
      <li className="group relative h-14 w-14 overflow-hidden rounded border border-neutral-300 dark:border-neutral-700">
        <img
          src={attachment.dataUrl}
          alt={attachment.name ?? 'attachment'}
          className="h-full w-full object-cover"
        />
        <RemoveButton onRemove={onRemove} />
      </li>
    );
  }
  return (
    <li
      className="group relative flex max-w-[16rem] items-center gap-2 rounded border border-neutral-300 bg-white px-2 py-1.5 text-[11px] dark:border-neutral-700 dark:bg-neutral-900"
      title={`${attachment.name} · ${formatSize(attachment.bytes)}`}
    >
      <span className="text-base" aria-hidden="true">
        {attachment.kind === 'document' ? '📄' : '📎'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{attachment.name}</p>
        <p className="text-neutral-500">
          {attachment.kind === 'document' ? 'PDF' : (attachment.mediaType || 'file')} ·{' '}
          {formatSize(attachment.bytes)}
        </p>
      </div>
      <RemoveButton onRemove={onRemove} />
    </li>
  );
}

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove attachment"
      className="absolute right-0 top-0 hidden rounded-bl bg-neutral-900/80 px-1 text-[11px] text-white group-hover:block"
    >
      ×
    </button>
  );
}
